import { useSearchParams } from "react-router-dom";

/**
 * URL-as-state for faceted lists. Filters/search/pagination all live in the query
 * string so views are shareable, bookmarkable, and survive refresh. Multi-select
 * facets are stored comma-separated (e.g. `?status=received,quoted`). Changing any
 * filter or the search resets pagination back to page 1.
 */
export function useFacetedParams() {
  const [params, setParams] = useSearchParams();

  const getValues = (key: string): string[] => {
    const v = params.get(key);
    return v ? v.split(",").filter(Boolean) : [];
  };

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.delete("page"); // filter/search change → back to page 1
    setParams(next, { replace: true });
  };

  const toggleValue = (key: string, value: string) => {
    const current = getValues(key);
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    setParam(key, next.length ? next.join(",") : null);
  };

  const clearAll = () => setParams(new URLSearchParams(), { replace: true });

  const search = params.get("q") ?? "";
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);

  // Active filter count (everything except search + pagination) — for a "clear" affordance.
  let activeFilters = 0;
  for (const [k, v] of params.entries()) {
    if (k === "q" || k === "page" || !v) continue;
    activeFilters += v.split(",").filter(Boolean).length;
  }

  return { params, getValues, setParam, toggleValue, clearAll, search, page, activeFilters };
}
